import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Button,
  Card,
  Chip,
  BottomSheet,
} from '../../components';
import { useApiKey } from '../../hooks';
import { getInspectionReport, reprocessInspection, generateIdempotencyKey } from '../../api';
import { formatDateTime, getStatusLabel, getStatusChipVariant, getGradeLabel, getGradeColor } from '../../utils';
import type { InspectionReport, InspectionStatus } from '../../types/api';

export const InspectionDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { inspectionId } = useParams<{ inspectionId: string }>();
  const { apiKey } = useApiKey();
  
  const [report, setReport] = useState<InspectionReport | null>(null);
  const [status, setStatus] = useState<InspectionStatus>('processing');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reprocessing, setReprocessing] = useState(false);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!inspectionId || !apiKey) return;
      
      setLoading(true);
      try {
        // Try to get report (will fail if not done)
        const reportData = await getInspectionReport(inspectionId, apiKey);
        setReport(reportData);
        setStatus('done');
      } catch (err) {
        // Report not ready, check status
        // In real app, would call status endpoint
        setStatus('processing');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [inspectionId, apiKey]);

  const handleReprocess = async () => {
    if (!inspectionId || !apiKey) return;
    
    if (!confirm('Reprocessar esta vistoria? Isso iniciará uma nova análise.')) return;
    
    setReprocessing(true);
    setError(null);
    
    try {
      await reprocessInspection(inspectionId, { apiKey, idempotencyKey: generateIdempotencyKey() });
      setStatus('processing');
      setReport(null);
      // Would start polling here
    } catch (err) {
      setError('Falha ao iniciar reprocessamento');
    } finally {
      setReprocessing(false);
    }
  };

  const handleBack = () => {
    navigate('/admin', { replace: true });
  };

  if (loading) {
    return (
      <Page appBar={{ title: 'Vistoria', leading: <span className="material-symbols-outlined">arrow_back</span>, leadingAction: handleBack }}>
        <div className="flex items-center justify-center flex-1">
          <span className="material-symbols-outlined animate-spin-slow text-primary text-3xl">sync</span>
        </div>
      </Page>
    );
  }

  return (
    <Page
      appBar={{
        title: 'Detalhes da Vistoria',
        subtitle: inspectionId?.slice(0, 8),
        leading: <span className="material-symbols-outlined">arrow_back</span>,
        leadingAction: handleBack,
      }}
      className="pb-24"
    >
      <div className="space-y-4">
        {/* Status Header */}
        <Card variant="elevated" padding="lg">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Chip variant={getStatusChipVariant(status)} icon={
                status === 'processing' ? <span className="material-symbols-outlined animate-spin-slow">sync</span> :
                status === 'done' ? <span className="material-symbols-outlined">check_circle</span> :
                <span className="material-symbols-outlined">error</span>
              }>
                {getStatusLabel(status)}
              </Chip>
            </div>
            {status === 'failed' && (
              <Button variant="outline" size="sm" onClick={handleReprocess} disabled={reprocessing} isLoading={reprocessing}>
                <span className="material-symbols-outlined">refresh</span>
                Reprocessar
              </Button>
            )}
          </div>

          {report && (
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-headline-lg">Nota {report.grade} — {getGradeLabel(report.grade)}</h2>
                <p className="font-body-sm text-on-surface-variant mt-1">Score: <span className="font-label-lg tabular-nums">{report.score}</span>/100</p>
              </div>
              <Chip
                variant={getGradeColor(report.grade) as any}
                icon={<span className="material-symbols-outlined">grade</span>}
              >
                {report.grade}
              </Chip>
            </div>
          )}
        </Card>

        {/* Report Summary */}
        {report && (
          <Card variant="elevated" padding="lg">
            <h3 className="font-label-lg mb-4">Resumo do Laudo</h3>
            
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 rounded-xl bg-surface-container">
                <p className="font-display-lg text-primary tabular-nums">{report.damages.length}</p>
                <p className="font-label-sm text-on-surface-variant">Avarias</p>
              </div>
              <div className="text-center p-3 rounded-xl bg-surface-container">
                <p className="font-display-lg text-on-surface tabular-nums">
                  {report.optional_items.filter(o => o.detected).length}
                </p>
                <p className="font-label-sm text-on-surface-variant">Opcionais</p>
              </div>
              <div className="text-center p-3 rounded-xl bg-surface-container">
                <p className="font-display-lg text-on-surface tabular-nums">{report.report_version}</p>
                <p className="font-label-sm text-on-surface-variant">Versão</p>
              </div>
            </div>

            <Button variant="primary" fullWidth onClick={() => setShowReport(true)} leftIcon={<span className="material-symbols-outlined">description</span>}>
              Ver Laudo Completo
            </Button>
          </Card>
        )}

        {/* Damages List */}
        {report && report.damages.length > 0 && (
          <Card variant="elevated" padding="md">
            <h3 className="font-label-lg mb-3 flex items-center gap-2">
              <span className="material-symbols-outlined text-warning">warning</span>
              Avarias Detectadas ({report.damages.length})
            </h3>
            <div className="space-y-3">
              {report.damages.map((damage) => (
                <Card key={damage.id} variant="default" padding="md">
                  <div className="flex items-start gap-3">
                    <div className="w-16 h-16 rounded-lg bg-error-container/50 flex items-center justify-center flex-shrink-0">
                      <span className="material-symbols-outlined text-error">crop_free</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-label-md">{damage.label}</h4>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Chip variant={damage.severity === 'grave' ? 'error' : damage.severity === 'moderado' ? 'warning' : 'info'} size="sm">
                          {damage.severity}
                        </Chip>
                      </div>
                      <p className="font-body-sm text-on-surface-variant mt-2 font-mono text-xs">
                        BBox: x={damage.bbox.x.toFixed(2)}, y={damage.bbox.y.toFixed(2)}, w={damage.bbox.width.toFixed(2)}, h={damage.bbox.height.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        )}

        {/* Optional Items */}
        {report && report.optional_items.length > 0 && (
          <Card variant="elevated" padding="md">
            <h3 className="font-label-lg mb-3 flex items-center gap-2">
              <span className="material-symbols-outlined text-secondary">tune</span>
              Itens Opcionais
            </h3>
            <div className="space-y-2">
              {report.optional_items.map((item, idx) => (
                <Card key={idx} variant="default" padding="md">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        item.detected ? 'bg-success-bg' : 'bg-surface-container-high'
                      }`}>
                        <span className={`material-symbols-outlined ${item.detected ? 'text-success' : 'text-on-surface-variant'}`}>
                          {item.detected ? 'check' : 'close'}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-label-md">{item.name}</h4>
                        <p className="font-body-sm text-on-surface-variant">
                          {item.detected ? 'Detectado' : 'Não detectado'} • {Math.round(item.confidence * 100)}% confiança
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        )}

        {/* Metadata */}
        <Card variant="outlined" padding="md">
          <h3 className="font-label-md mb-2">Informações Técnicas</h3>
          <div className="space-y-1 text-body-sm">
            <p>ID: <code className="font-mono tabular-nums">{inspectionId}</code></p>
            <p>Status: <span className="font-label-md">{getStatusLabel(status)}</span></p>
            {report && <p>Concluído: {formatDateTime(report.completed_at)}</p>}
            {report && <p>Versão do laudo: {report.report_version}</p>}
          </div>
        </Card>

        {error && (
          <Card variant="outlined" padding="md" className="bg-error-container">
            <p className="font-body-sm text-on-error-container">{error}</p>
          </Card>
        )}
      </div>

      {/* Full Report Bottom Sheet */}
      <BottomSheet
        isOpen={showReport}
        onClose={() => setShowReport(false)}
        title="Laudo Completo"
        subtitle={report ? `${report.plate} • ${formatDateTime(report.completed_at)}` : ''}
      >
        {report && (
          <div className="space-y-6 max-h-[80vh] overflow-y-auto">
            {/* Grade & Score */}
            <Card variant="elevated" padding="lg" className="text-center">
              <Chip
                variant={getGradeColor(report.grade) as any}
                size="sm"
                className="mb-3"
              >
                Nota {report.grade} — {getGradeLabel(report.grade)}
              </Chip>
              <p className="font-display-lg text-primary tabular-nums">{report.score}</p>
              <p className="font-label-sm text-on-surface-variant mt-1">Score de 0 a 100</p>
            </Card>

            {/* Damages with images */}
            {report.damages.length > 0 && (
              <div>
                <h3 className="font-label-lg mb-3">Avarias com Imagens</h3>
                <div className="space-y-4">
                  {report.damages.map((damage) => (
                    <Card key={damage.id} variant="default" padding="md">
                      <div className="flex items-start gap-3">
                        {/* Annotated Image */}
                        <img 
                          src={damage.annotated_photo_url} 
                          alt={damage.label}
                          className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-label-md">{damage.label}</h4>
                          <Chip variant={damage.severity === 'grave' ? 'error' : damage.severity === 'moderado' ? 'warning' : 'info'} size="sm">
                            {damage.severity}
                          </Chip>
                          <p className="font-body-sm text-on-surface-variant mt-2">
                            Confiança: {Math.round((damage as any).confidence * 100 || 85)}%
                          </p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Optional Items */}
            {report.optional_items.length > 0 && (
              <div>
                <h3 className="font-label-lg mb-3">Itens Opcionais</h3>
                <div className="space-y-2">
                  {report.optional_items.map((item, idx) => (
                    <Card key={idx} variant="default" padding="md">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            item.detected ? 'bg-success-bg' : 'bg-surface-container-high'
                          }`}>
                            <span className={`material-symbols-outlined ${item.detected ? 'text-success' : 'text-on-surface-variant'}`}>
                              {item.detected ? 'check' : 'close'}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-label-md">{item.name}</h4>
                            <p className="font-body-sm text-on-surface-variant">
                              {item.detected ? 'Detectado' : 'Não detectado'} • {Math.round(item.confidence * 100)}% confiança
                            </p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </BottomSheet>
    </Page>
  );
};